/**
 * LIVE GAME VIEW — what is happening right now. Its own page, its own menu
 * entry, no mode switch. Game Results stays the history tab and is not touched
 * by this file.
 *
 * It refreshes itself every 10 seconds in place, so the table never jumps or
 * flickers while it is being read, and the countdown ticks every second
 * without asking the server anything.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Loader2, Lock, Radio, Users, MousePointerClick, Trophy, Ticket, Home, Store } from 'lucide-react';
import api from '../lib/api';
import { useAuth } from '../lib/auth';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import {
    Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../components/ui/select';
import {
    Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '../components/ui/table';
import { parseUtc } from '../lib/datetime';
import { format } from 'date-fns';
import { GameDonuts, buildGameDonuts, buildGiftCardDonut } from '../components/shared/GameDonuts';
import {
    GameFilterBar, useGameFilters, rowMatches, filterLabel,
} from '../components/shared/GameFilterBar';
import { PlayerStatusPill, StarPickCell, StarPickCount, starPickCounts } from '../components/shared/GamePlayerChips';
import { PlayerHistoryPanel } from '../components/shared/PlayerHistoryPanel';
import { BroadcastControl } from '../components/live_game/BroadcastControl';
import { ChatModeration } from '../components/live_game/ChatModeration';
import { AnnounceControl } from '../components/live_game/AnnounceControl';

const REFRESH_MS = 10000;
const ALL_LOCATIONS = 'all';
// A convenience only — the server decides what this user may actually see.
const ORG_MEMORY = 'yot_game_org';

const stamp = (value) => {
    if (!value) return '—';
    const d = parseUtc(value);
    return d ? format(d, 'MMM d, HH:mm:ss') : '—';
};


/**
 * A team crest beside its name. The box never changes size, so a logo loading
 * late cannot shift the header. No logo (or a logo that fails) falls back to a
 * round badge in the team's own colour, never a broken image.
 */
const TeamMark = ({ abbrev, logo, color, testId }) => {
    const [broken, setBroken] = useState(false);
    const show = logo && !broken;
    return (
        <span className="inline-flex h-7 w-7 md:h-9 md:w-9 shrink-0 items-center justify-center"
              data-testid={testId}>
            {show ? (
                <img src={logo} alt={abbrev || ''} loading="lazy" decoding="async"
                     onError={() => setBroken(true)}
                     className="h-full w-full object-contain" />
            ) : (
                <span className="flex h-full w-full items-center justify-center rounded-full
                                 text-[10px] md:text-xs font-bold tracking-tight"
                      style={color
                          ? { background: color, color: '#fff' }
                          : { background: 'var(--muted, rgba(148,163,184,0.18))' }}>
                    {(abbrev || '?').slice(0, 3)}
                </span>
            )}
        </span>
    );
};

/** No tick, no page. A direct link lands here instead of a blank screen; the
 *  tick itself is in the permissions screen's "Goal Game" group. */
const NoGoalAccess = () => {
    const { t } = useTranslation();
    return (
        <div className="p-8 text-center" data-testid="goal-no-access">
            <Lock className="w-10 h-10 text-muted-foreground/40 mx-auto mb-3" />
            <p className="text-lg font-medium">{t('goalAccess.title')}</p>
            <p className="text-sm text-muted-foreground">{t('goalAccess.body')}</p>
        </div>
    );
};

function LiveGameViewPage({ managerToken = null, onSessionEnded = null }) {
    const neo = managerToken
        ? { headers: { 'X-Manager-Session': managerToken } } : {};
    const { t } = useTranslation();
    const { isSuperAdmin } = useAuth();
    const [params, setParams] = useSearchParams();
    const { active, toggle, clear, count: filterCount } = useGameFilters(params, setParams);
    const [org, setOrg] = useState(
        params.get('organization_id') || localStorage.getItem(ORG_MEMORY) || '');

    const [data, setData] = useState(null);
    const [firstLoad, setFirstLoad] = useState(true);
    const [location, setLocation] = useState(ALL_LOCATIONS);
    const [tick, setTick] = useState(0);
    const [openRow, setOpenRow] = useState(null);
    const [sponsorFilter, setSponsorFilter] = useState('');
    const fetchedAt = useRef(Date.now());

    const load = useCallback(async () => {
        try {
            const res = await api.get('/live-game', {
                ...neo,
                params: {
                    organization_id: org || undefined,
                    location_id: location !== ALL_LOCATIONS ? location : undefined,
                },
            });
            setData(res.data);
            fetchedAt.current = Date.now();
            // The server may have chosen for us (nothing picked yet): follow it.
            if (res.data.organization_id && res.data.organization_id !== org) {
                setOrg(res.data.organization_id);
            }
        } catch (e) {
            // A dropped poll changes nothing on screen. A NEO session that has
            // ended is different: the page must say so, never show a dead board.
            if (e.response?.status === 401 && onSessionEnded) onSessionEnded();
        } finally {
            setFirstLoad(false);
        }
    }, [org, location, managerToken]);

    useEffect(() => {
        load();
        const id = setInterval(load, REFRESH_MS);
        return () => clearInterval(id);
    }, [load]);

    // One second heartbeat for the countdown and the "updated" line.
    useEffect(() => {
        const id = setInterval(() => setTick((n) => n + 1), 1000);
        return () => clearInterval(id);
    }, []);

    const game = data?.game || {};
    const allRows = data?.rows || [];
    // Donuts count everybody, so a slice never vanishes while it is the filter.
    const donuts = useMemo(
        () => [...buildGameDonuts(allRows, t, { keepEmpty: true }),
               buildGiftCardDonut(data?.gift_cards, t)],
        [allRows, data?.gift_cards, t]);

    // One short line under the title of a card that has nothing in it yet.
    const emptyHint = data?.game?.state === 'no_game'
        ? t('gameBoard.empty.noGame', 'Waiting for the next game')
        : t('gameBoard.empty.nothingYet', 'Nothing yet tonight');
    const sponsorNames = useMemo(
        () => [...new Set(allRows.map((r) => r.sponsor).filter(Boolean))], [allRows]);
    const anySponsor = sponsorNames.length > 0;
    // A fight-card night: every player's own fight-by-fight tally.
    const anyFightPicks = allRows.some((r) => r.fight_picks);
    const rows = useMemo(
        () => allRows.filter((r) => rowMatches(r, active))
            .filter((r) => !sponsorFilter || r.sponsor === sponsorFilter),
        [allRows, active, sponsorFilter]);
    // Every row of THIS event, so the number does not move when a filter is on.
    const starCounts = useMemo(() => starPickCounts(allRows), [allRows]);
    // Every count on the page follows the filter, not just the list.
    const counts = useMemo(() => ({
        players: rows.length,
        picks: rows.filter((r) => r.winner_pick || r.star_pick).length,
        scratches: rows.reduce((n, r) => n + (Number(r.scratch_count) || 0), 0),
        wins: rows.reduce((n, r) => n + (Number(r.win_count) || 0), 0),
    }), [rows]);
    const locations = data?.locations || [];
    const showLocationColumn = locations.length > 1 && location === ALL_LOCATIONS;

    const countdown = useMemo(() => {
        if (game.state !== 'before' || !game.start_time_utc) return null;
        const start = parseUtc(game.start_time_utc);
        if (!start) return null;
        const left = Math.floor((start.getTime() - Date.now()) / 1000);
        if (left <= 0) return null;
        const h = Math.floor(left / 3600);
        const m = Math.floor((left % 3600) / 60);
        const s = left % 60;
        return h > 0
            ? t('liveGame.countdownHm', '{{h}} h {{m}} min', { h, m })
            : t('liveGame.countdownMs', '{{m}} min {{s}} s', { m, s });
    }, [game.state, game.start_time_utc, tick, t]);

    const sinceSeconds = Math.max(0, Math.round((Date.now() - fetchedAt.current) / 1000));
    const startWord = t(`liveGame.start.${game.start_word || 'game_start'}`,
        t('liveGame.start.game_start', 'Game starts in'));

    const ticker = () => {
        if (game.state === 'feed_unavailable') {
            return (
                <div data-testid="live-game-feed-down">
                    <p className="text-lg md:text-xl font-bold text-orange-500">
                        {t('liveGame.feedDown', 'We cannot reach the score service right now')}
                    </p>
                    <p className="text-sm text-muted-foreground">
                        {t('liveGame.lastChecked', 'Last checked {{when}}', { when: stamp(game.last_checked_at) })}
                    </p>
                </div>
            );
        }
        if (!game.state || game.state === 'no_game') {
            return (
                <div data-testid="live-game-no-game">
                    <p className="text-lg md:text-xl font-bold">
                        {t('liveGame.noGame', 'No game is set up for tonight')}
                    </p>
                    <Button asChild variant="outline" className="mt-3 min-h-[48px]">
                        {/* Keeps the venue already chosen here. */}
                        <Link to={`/game-results?organization_id=${data?.organization_id || ''}`}
                              data-testid="live-game-see-results-btn">
                            {t('liveGame.seeResults', 'See Game Results')}
                        </Link>
                    </Button>
                </div>
            );
        }
        return (
            <div className="flex flex-wrap items-center gap-x-6 gap-y-2" data-testid="live-game-ticker-body">
                <p className="flex items-center gap-2 text-xl md:text-3xl font-bold tracking-tight"
                   data-testid="live-game-label">
                    <TeamMark abbrev={game.away_abbrev} logo={game.away_logo}
                              color={game.away_color} testId="live-game-label-away-logo" />
                    <span>{game.away_abbrev || ''}</span>
                    <span className="text-muted-foreground text-base md:text-xl font-normal">
                        {t('liveGame.at', 'at')}
                    </span>
                    <TeamMark abbrev={game.home_abbrev} logo={game.home_logo}
                              color={game.home_color} testId="live-game-label-home-logo" />
                    <span>{game.home_abbrev || (game.label ? '' : '—')}</span>
                </p>
                {game.state === 'before' && (
                    <p className="text-lg md:text-2xl font-semibold text-emerald-500" data-testid="live-game-countdown">
                        {countdown ? `${startWord} ${countdown}` : t('liveGame.startingSoon', 'Starting any moment')}
                    </p>
                )}
                {game.state !== 'before' && (
                    <p className="flex items-center gap-2 text-xl md:text-3xl font-bold"
                       data-testid="live-game-score">
                        <TeamMark abbrev={game.away_abbrev} logo={game.away_logo}
                                  color={game.away_color} testId="live-game-score-away-logo" />
                        <span>{game.away_abbrev} {game.away_score ?? 0}</span>
                        <span className="text-muted-foreground">—</span>
                        <TeamMark abbrev={game.home_abbrev} logo={game.home_logo}
                                  color={game.home_color} testId="live-game-score-home-logo" />
                        <span>{game.home_abbrev} {game.home_score ?? 0}</span>
                    </p>
                )}
                {game.state === 'live' && game.period && (
                    <p className="text-base md:text-lg text-muted-foreground" data-testid="live-game-period">
                        {game.intermission
                            ? t('liveGame.intermission', 'Break after {{word}} {{n}}', { word: game.segment_word, n: game.period })
                            : `${game.segment_word} ${game.period}`}
                    </p>
                )}
                {game.state === 'over' && (
                    <Badge variant="secondary" className="text-sm" data-testid="live-game-over">
                        {t('liveGame.gameOver', 'Game over')}
                    </Badge>
                )}
            </div>
        );
    };

    const waiting = (
        <span className="text-xs text-orange-600 dark:text-orange-400">
            {t('liveGame.waiting', 'Waiting')}
        </span>
    );

    const scratchCells = (row) => (row.scratch_count === 0 ? {
        result: waiting, prize: waiting, voucher: waiting, status: waiting,
    } : {
        result: t(`liveGame.result.${row.result}`, row.result || '—'),
        prize: row.prize || '—',
        voucher: row.voucher || '—',
        status: row.voucher_status
            ? t(`liveGame.state.${row.voucher_status}`, row.voucher_status) : '—',
    });

    const rowTag = (row) => (row.demo ? (
        <Badge variant="outline" className="ml-2 text-[10px] px-1.5 py-0"
               data-testid={`live-game-demo-tag-${row.row_id}`}>
            {t('liveGame.demoTag', 'Demo')}
        </Badge>
    ) : null);

    if (firstLoad) {
        return <div className="p-8" data-testid="live-game-loading"><Loader2 className="w-6 h-6 animate-spin text-muted-foreground" /></div>;
    }

    return (
        <div className="p-4 sm:p-6 space-y-5 max-w-[1500px] mx-auto" data-testid="live-game-page">
            {/* The ticker stays at the top on every size. */}
            <div className="sticky top-0 z-20 -mx-4 sm:-mx-6 px-4 sm:px-6 py-4 bg-background/95 backdrop-blur border-b"
                 data-testid="live-game-ticker">
                <div className="flex flex-wrap items-center justify-between gap-3">
                    {ticker()}
                    <div className="flex items-center gap-2 text-xs text-muted-foreground">
                        <span className="relative flex h-2.5 w-2.5" data-testid="live-game-dot">
                            <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75" />
                            <span className="relative inline-flex rounded-full h-2.5 w-2.5 bg-emerald-500" />
                        </span>
                        <span data-testid="live-game-updated">
                            {t('liveGame.updated', 'Updated {{n}} seconds ago', { n: sinceSeconds })}
                        </span>
                    </div>
                </div>
            </div>

            <div className="flex flex-wrap items-center justify-between gap-3">
                <div>
                    <h1 className="text-2xl font-bold flex items-center gap-2" data-testid="live-game-org">
                        <Radio className="w-6 h-6 text-emerald-500" />
                        {t('liveGame.title', 'Live Game View')}
                        {data?.organization_name ? ` — ${data.organization_name}` : ''}
                    </h1>
                </div>
                {(data?.organizations || []).length > 1 && (
                    <Select value={org} onValueChange={(v) => {
                        localStorage.setItem(ORG_MEMORY, v);
                        setLocation(ALL_LOCATIONS);
                        setOrg(v);
                    }}>
                        <SelectTrigger className="w-64 min-h-[48px]" data-testid="live-game-org-picker">
                            <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                            {(data?.organizations || []).map((o) => (
                                <SelectItem key={o.id} value={o.id} data-testid={`live-game-org-${o.id}`}>
                                    {o.name}{o.demo ? ` · ${t('liveGame.demoTag', 'Demo')}` : ''}
                                </SelectItem>
                            ))}
                        </SelectContent>
                    </Select>
                )}
                {locations.length > 1 && (
                    <Select value={location} onValueChange={setLocation}>
                        <SelectTrigger className="w-56 min-h-[48px]" data-testid="live-game-location-picker">
                            <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                            <SelectItem value={ALL_LOCATIONS}>{t('liveGame.allLocations', 'All locations')}</SelectItem>
                            {locations.map((l) => (
                                <SelectItem key={l.id} value={l.id} data-testid={`live-game-location-${l.id}`}>
                                    {l.name}
                                </SelectItem>
                            ))}
                        </SelectContent>
                    </Select>
                )}
            </div>

            {/* Broadcast to players — the switch, who is on the board, and a
                preview of the board itself. */}
            <BroadcastControl orgId={data?.organization_id} neo={neo} t={t}
                              isSuperAdmin={isSuperAdmin()} />

            {/* One line pinned on top of the players' board. */}
            <AnnounceControl orgId={data?.organization_id} neo={neo} t={t} />

            {/* Tonight's chat — delete a message, mute a player for tonight.
                Shown only where "Live chat" is on. */}
            <ChatModeration orgId={data?.organization_id} neo={neo} t={t} />

            <div className="grid grid-cols-2 lg:grid-cols-4 gap-3" data-testid="live-game-summary">
                {[
                    { icon: Users, key: 'playersNow', fallback: 'Players now', value: counts.players, id: 'players' },
                    { icon: MousePointerClick, key: 'picksMade', fallback: 'Picks made', value: counts.picks, id: 'picks' },
                    { icon: Ticket, key: 'scratches', fallback: 'Scratches', value: counts.scratches, id: 'scratches' },
                    { icon: Trophy, key: 'wins', fallback: 'Wins', value: counts.wins, id: 'wins' },
                ].map(({ icon: Icon, key, fallback, value, id }) => (
                    <Card key={id} data-testid={`live-game-sum-${id}`}>
                        <CardContent className="p-4">
                            <div className="flex items-center gap-2 text-muted-foreground mb-1">
                                <Icon className="w-4 h-4" />
                                <span className="text-xs">{t(`liveGame.sum.${key}`, fallback)}</span>
                            </div>
                            <p className="text-2xl md:text-3xl font-bold">{value}</p>
                        </CardContent>
                    </Card>
                ))}
            </div>

            <GameDonuts donuts={donuts} active={active} onToggle={toggle} prefix="live-game"
                        emptyHint={emptyHint} />

            <GameFilterBar active={active} onToggle={toggle} onClear={clear}
                           count={filterCount} t={t} prefix="live-game"
                           labelFor={(id, key) => filterLabel(t, id, key)} />

            {allRows.length > 0 && rows.length === 0 ? (
                <Card><CardContent className="py-12 text-center" data-testid="live-game-filtered-empty">
                    <p className="text-lg font-medium">{t('gameBoard.noneMatch', 'No players match these filters')}</p>
                </CardContent></Card>
            ) : rows.length === 0 ? (
                <Card><CardContent className="py-12 text-center" data-testid="live-game-empty">
                    <p className="text-lg font-medium">{t('liveGame.nobodyTitle', 'Nobody has joined yet')}</p>
                    <p className="text-sm text-muted-foreground">
                        {t('liveGame.nobodyBody', 'The first player will show up here within a few seconds.')}
                    </p>
                </CardContent></Card>
            ) : (
                <>
                    {/* Tonight's at-home sponsors: filter the list by one. */}
                    {anySponsor && (
                        <div className="flex flex-wrap items-center gap-2 mb-3" data-testid="live-game-sponsor-filter">
                            <Button size="sm" variant={sponsorFilter ? 'outline' : 'default'}
                                    data-testid="live-game-sponsor-all"
                                    onClick={() => setSponsorFilter('')}>
                                {t('goalGame.boardFilterAll')}
                            </Button>
                            {sponsorNames.map((name) => (
                                <Button key={name} size="sm"
                                        variant={sponsorFilter === name ? 'default' : 'outline'}
                                        data-testid={`live-game-sponsor-pick-${name}`}
                                        onClick={() => setSponsorFilter(name)}>
                                    {name}
                                </Button>
                            ))}
                        </div>
                    )}
                    {/* iPad and laptop: the big readable table. */}
                    <Card className="hidden md:block">
                        <CardContent className="p-0 overflow-x-auto">
                            <Table data-testid="live-game-table">
                                <TableHeader>
                                    <TableRow>
                                        <TableHead>{t('liveGame.col.player', 'Player')}</TableHead>
                                        <TableHead>{t('gameBoard.playerStatus', 'Status')}</TableHead>
                                        <TableHead>{t('liveGame.col.email', 'Email')}</TableHead>
                                        {showLocationColumn && <TableHead>{t('liveGame.col.location', 'Location')}</TableHead>}
                                        <TableHead>{t('liveGame.col.where', 'Where')}</TableHead>
                                        {anySponsor && <TableHead>{t('goalGame.sponsorColumn')}</TableHead>}
                                        <TableHead>{t('liveGame.col.joined', 'Joined')}</TableHead>
                                        <TableHead>{t('liveGame.col.winnerPick', 'Winner pick')}</TableHead>
                                        {anyFightPicks && <TableHead>{t('gameResults.col.fight_picks')}</TableHead>}
                                        <TableHead>{t('liveGame.col.starPick', 'Star pick')}</TableHead>
                                        <TableHead>{t('gameBoard.starPickCount', 'Picks')}</TableHead>
                                        <TableHead>{t('liveGame.col.result', 'Result')}</TableHead>
                                        <TableHead>{t('liveGame.col.prize', 'Prize')}</TableHead>
                                        <TableHead>{t('liveGame.col.voucher', 'Voucher')}</TableHead>
                                        <TableHead>{t('liveGame.col.voucherStatus', 'Voucher status')}</TableHead>
                                    </TableRow>
                                </TableHeader>
                                <TableBody>
                                    {rows.map((row) => {
                                        const c = scratchCells(row);
                                        return (
                                            <TableRow key={row.row_id}
                                                      onClick={() => setOpenRow(row)}
                                                      className={`cursor-pointer animate-in fade-in duration-300 ${row.demo ? '' : 'bg-emerald-500/5'}`}
                                                      data-testid={`live-game-row-${row.row_id}`}>
                                                <TableCell className="font-medium">
                                                    {row.player || '—'}{rowTag(row)}
                                                </TableCell>
                                                <TableCell>
                                                    <PlayerStatusPill status={row.status} t={t}
                                                                      testId={`live-game-status-${row.row_id}`} />
                                                </TableCell>
                                                <TableCell>{row.email || '—'}</TableCell>
                                                {showLocationColumn && <TableCell>{row.location || '—'}</TableCell>}
                                                {anySponsor && (
                                                    <TableCell data-testid={`live-game-sponsor-${row.row_id}`}>
                                                        {row.sponsor || '—'}
                                                    </TableCell>
                                                )}
                                                <TableCell>
                                                    {row.where ? (
                                                        <span className="inline-flex items-center gap-1">
                                                            {row.where === 'at_home' ? <Home className="w-3.5 h-3.5" /> : <Store className="w-3.5 h-3.5" />}
                                                            {t(`liveGame.where.${row.where}`, row.where)}
                                                        </span>
                                                    ) : '—'}
                                                </TableCell>
                                                <TableCell className="whitespace-nowrap">{stamp(row.at)}</TableCell>
                                                <TableCell>{row.winner_pick || '—'}</TableCell>
                                                {anyFightPicks && (
                                                    <TableCell data-testid={`live-game-fight-picks-${row.row_id}`}>
                                                        {row.fight_picks || '—'}
                                                    </TableCell>
                                                )}
                                                <TableCell>
                                                    <StarPickCell name={row.star_pick} t={t}
                                                                  testId={`live-game-star-${row.row_id}`} />
                                                </TableCell>
                                                <TableCell>
                                                    <StarPickCount count={starCounts[(row.star_pick || '').trim()]} t={t}
                                                                   testId={`live-game-star-count-${row.row_id}`} />
                                                </TableCell>
                                                <TableCell>{c.result}</TableCell>
                                                <TableCell>{c.prize}</TableCell>
                                                <TableCell className="font-mono text-xs">{c.voucher}</TableCell>
                                                <TableCell>{c.status}</TableCell>
                                            </TableRow>
                                        );
                                    })}
                                </TableBody>
                            </Table>
                        </CardContent>
                    </Card>

                    {/* Phone: one card per player, no sideways scrolling. */}
                    <div className="md:hidden space-y-3" data-testid="live-game-cards">
                        {rows.map((row) => {
                            const c = scratchCells(row);
                            return (
                                <Card key={row.row_id} onClick={() => setOpenRow(row)}
                                      className={`animate-in fade-in duration-300 ${row.demo ? '' : 'ring-1 ring-emerald-500/40'}`}
                                      data-testid={`live-game-card-${row.row_id}`}>
                                    <CardContent className="p-4 space-y-1.5">
                                        <p className="text-base font-semibold">{row.player || '—'}{rowTag(row)}</p>
                                        <PlayerStatusPill status={row.status} t={t}
                                                          testId={`live-game-card-status-${row.row_id}`} />
                                        {row.email && <p className="text-sm text-muted-foreground break-all">{row.email}</p>}
                                        <p className="text-sm">{stamp(row.at)}</p>
                                        {showLocationColumn && row.location && <p className="text-sm">{row.location}</p>}
                                        {row.sponsor && (
                                            <p className="text-sm" data-testid={`live-game-card-sponsor-${row.row_id}`}>
                                                {t('goalGame.sponsorColumn')}: {row.sponsor}
                                            </p>
                                        )}
                                        <p className="text-sm">
                                            {t('liveGame.col.winnerPick', 'Winner pick')}: {row.winner_pick || '—'}
                                            {row.fight_picks ? ` · ${row.fight_picks}` : ''}
                                        </p>
                                        <p className="text-sm flex items-center gap-2">
                                            <span>{t('liveGame.col.starPick', 'Star pick')}:</span>
                                            <StarPickCell name={row.star_pick} t={t}
                                                          testId={`live-game-card-star-${row.row_id}`} />
                                            <StarPickCount count={starCounts[(row.star_pick || '').trim()]} t={t}
                                                           testId={`live-game-card-star-count-${row.row_id}`} />
                                        </p>
                                        <p className="text-sm">
                                            {t('liveGame.col.result', 'Result')}: {c.result}
                                        </p>
                                        <p className="text-sm">
                                            {t('liveGame.col.prize', 'Prize')}: {c.prize}
                                        </p>
                                    </CardContent>
                                </Card>
                            );
                        })}
                    </div>
                </>
            )}
            {data?.demo_players_missing && isSuperAdmin() && (
                <p className="text-sm text-muted-foreground" data-testid="live-game-demo-missing">
                    <Link to={`/patron-points?organization_id=${data?.organization_id || ''}`}
                          className="underline underline-offset-4">
                        {t('liveGame.addDemoPlayers', 'Add demo players first')}
                    </Link>
                </p>
            )}

            {/* Tap a player to see their whole history — the same panel Game
                Results opens. */}
            <PlayerHistoryPanel open={!!openRow} onOpenChange={(v) => !v && setOpenRow(null)}
                                organizationId={data?.organization_id} player={openRow}
                                managerToken={managerToken} t={t} prefix="live-game" />
        </div>
    );
}

export default function LiveGameView(props) {
    const { hasPermission } = useAuth();
    // A signed NEO manager session reaches this page through its own token and
    // keeps the access it has always had.
    if (!props.managerToken && !hasPermission('view_live_game')) return <NoGoalAccess />;
    return <LiveGameViewPage {...props} />;
}
